import { useState } from "react";
import { CaretLeft, CaretRight, ArrowRight } from "@phosphor-icons/react";
import { T } from "../../themes";
import { TODAY_ISO, fmtDate, fmtDuration } from "../../constants";
import AppHeader from "../../components/AppHeader";

export default function CalendarScreen({sessions,onSelectSession,persona,darkMode,setDarkMode,profile,onNavigate}){
  const [viewDate,setViewDate]=useState(()=>new Date());
  const year=viewDate.getFullYear();
  const month=viewDate.getMonth();
  const monthName=viewDate.toLocaleDateString("en-GB",{month:"long",year:"numeric"});

  // Build set of dates with sessions this month
  const sessionDates={};
  sessions.forEach(s=>{
    if(!s.date) return;
    const d=new Date(s.date+"T00:00:00");
    if(d.getFullYear()===year&&d.getMonth()===month){
      sessionDates[s.date]=(sessionDates[s.date]||[]).concat(s);
    }
  });

  // Calendar grid
  const firstDay=new Date(year,month,1).getDay(); // 0=Sun
  const daysInMonth=new Date(year,month+1,0).getDate();
  const startOffset=(firstDay+6)%7; // Monday start
  const cells=[];
  for(let i=0;i<startOffset;i++) cells.push(null);
  for(let d=1;d<=daysInMonth;d++) cells.push(d);
  while(cells.length%7!==0) cells.push(null);

  const todayISO_=TODAY_ISO();

  function prev(){setViewDate(d=>{const n=new Date(d);n.setMonth(n.getMonth()-1);return n;});}
  function next(){setViewDate(d=>{const n=new Date(d);n.setMonth(n.getMonth()+1);return n;});}

  // Weekly volume bars for this month
  const weekVolumes=[];
  let wStart=new Date(year,month,1);
  wStart.setDate(wStart.getDate()-(((wStart.getDay()+6)%7)));
  for(let w=0;w<6;w++){
    let vol=0;
    for(let d=0;d<7;d++){
      const _dt=new Date(wStart.getTime()+((w*7+d)*86400000));
      const dISO=`${_dt.getFullYear()}-${String(_dt.getMonth()+1).padStart(2,"0")}-${String(_dt.getDate()).padStart(2,"0")}`;
      for(const s of (sessionDates[dISO]||[])){
        for(const ex of (s.exercises||[])) for(const st of (ex.sets||[])) if(st.done) vol+=(st.weight||0)*(st.reps||0);
      }
    }
    weekVolumes.push(vol);
  }
  const maxVol=Math.max(...weekVolumes,1);

  return(
    <div style={{background:T.pageBg,minHeight:"100dvh"}}>
      <AppHeader
        title="Calendar"
        subtitle="Your training schedule"
        currentApp="lift"
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        username={profile?.username}
        rightContent={<>
          <span style={{fontSize:11,fontFamily:T.fontSans,fontWeight:600,color:T.inkMuted}}>{monthName}</span>
          <button onClick={prev} style={{width:44,height:44,borderRadius:12,background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",margin:"-8px 0"}}>
            <CaretLeft size={16} weight="bold" color={T.inkMuted}/>
          </button>
          <button onClick={next} style={{width:44,height:44,borderRadius:12,background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",margin:"-8px 0"}}>
            <CaretRight size={16} weight="bold" color={T.inkMuted}/>
          </button>
        </>}
      />

      <div style={{padding:"0 16px 8px"}}>
        {/* Day labels */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
          {["M","T","W","T","F","S","S"].map((d,i)=>(
            <div key={i} style={{textAlign:"center",fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",padding:"4px 0"}}>{d}</div>
          ))}
        </div>
        {/* Calendar grid */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:16}}>
          {cells.map((day,i)=>{
            if(!day) return <div key={i}/>;
            const iso=`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
            const hasSess=sessionDates[iso]&&sessionDates[iso].length>0;
            const isToday=iso===todayISO_;
            return(
              <button key={i} onClick={()=>hasSess&&onSelectSession&&onSelectSession(sessionDates[iso][0])}
                style={{
                  aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                  background:hasSess?T.primary:isToday?T.surfaceLight:T.card,
                  border:isToday?`2px solid ${T.inkDark}`:`1px solid ${T.cardBorder}`,
                  borderRadius:T.radiusSm,
                  cursor:hasSess?"pointer":"default",
                  position:"relative",
                }}>
                <span style={{fontSize:12,fontFamily:T.fontSans,fontWeight:700,color:hasSess?"white":isToday?T.inkDark:T.inkMuted}}>{day}</span>
                {hasSess&&sessionDates[iso].length>1&&(
                  <span style={{fontSize:9,fontFamily:T.fontSans,color:"rgba(255,255,255,0.8)",fontWeight:600}}>{sessionDates[iso].length}x</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Weekly volume bars — hidden when no data */}
        {weekVolumes.some(v=>v>0)&&(
          <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radius,padding:"12px 14px",marginBottom:14}}>
            <p style={{margin:"0 0 8px",fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted}}>Weekly volume</p>
            <div style={{display:"flex",alignItems:"flex-end",gap:4,height:40}}>
              {weekVolumes.map((v,i)=>(
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                  <div style={{width:"100%",height:v>0?Math.max(4,(v/maxVol)*36):2,background:v>0?T.primary:T.surfaceLight,borderRadius:"2px 2px 0 0"}}/>
                  <span style={{fontSize:10,fontFamily:T.fontSans,color:T.inkMuted}}>W{i+1}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sessions this month */}
        <p style={{margin:"0 0 8px",fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted}}>
          {Object.values(sessionDates).flat().length} session{Object.values(sessionDates).flat().length!==1?"s":""} this month
        </p>
        {Object.keys(sessionDates).sort((a,b)=>b<a?-1:1).map(date=>(
          sessionDates[date].map(s=>(
            <button key={s.id} onClick={()=>onSelectSession&&onSelectSession(s)}
              style={{display:"block",width:"100%",textAlign:"left",background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radius,padding:"12px 14px",marginBottom:6,cursor:"pointer"}}>
              <p style={{margin:0,fontFamily:T.fontDisplay,fontSize:14,fontWeight:800,color:T.inkDark}}>{s.name||"Workout"}</p>
              <p style={{margin:"2px 0 0",fontSize:10,fontFamily:T.fontSans,color:T.inkMuted}}>
                {fmtDate(s.date)} · {(s.exercises||[]).length} exercises · {fmtDuration(s.duration||0)}{s.calories_burned>0?` · ~${s.calories_burned} kcal`:""}
              </p>
            </button>
          ))
        ))}
        {Object.keys(sessionDates).length===0&&(
          <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radius,padding:"20px 16px",textAlign:"center"}}>
            <p style={{fontSize:13,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkDark,margin:"0 0 4px"}}>No workouts this month yet</p>
            <p style={{fontSize:11,fontFamily:T.fontSans,color:T.inkMuted,margin:"0 0 14px",lineHeight:1.4}}>Start one from your routines or programmes.</p>
            <div style={{display:"flex",gap:8,justifyContent:"center"}}>
              <button onClick={()=>onNavigate&&onNavigate("routines")} style={{background:T.orange,border:"none",borderRadius:T.radiusSm,padding:"9px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                <span style={{fontSize:12,fontWeight:700,color:"#fff",fontFamily:T.fontSans}}>Start a workout</span>
                <ArrowRight size={12} color="#fff"/>
              </button>
              <button onClick={()=>onNavigate&&onNavigate("programmes")} style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,padding:"9px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                <span style={{fontSize:12,fontWeight:700,color:T.inkDark,fontFamily:T.fontSans}}>View programmes</span>
                <ArrowRight size={12} color={T.inkMuted}/>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
